import { useCallback, useEffect, useMemo, useState } from 'react';
import { Cookies } from 'react-cookie';
import qs from 'qs';
import { DateTime } from 'luxon';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { EventEmitter } from 'events';
import { Utils } from '@sparrow-marketing/contracts';

import { IInsights } from './types';
import { trackEvent } from '../../helpers/external-tools-helper';
import {
  API_BASE_URL,
  endpoints,
  environment,
  LOCAL_STORAGE_TYPES,
} from '../../constants';
import { useNetwork } from '../../hooks';
import {
  InsightsDateRange,
  InsightsState,
  IsChartLoading,
  IsLoader,
  OldInsightsState,
  SelectedAccountId,
  SelectedInsightsState,
  InsightAccountLoader,
} from '.';
import { ClientContainer } from '../../providers';
import useLocalStorage from '../../hooks/localstorage';
import {
  mergeParameters,
  parseQueryParameters,
  QUERY_PARAMS,
} from '../../helpers/query-params-utils';
import API from '../../api';
import { AllMetricsListState, MetricsChartState } from '..';
import useCurrency from '../../hooks/use-currency';
import { formatInsights } from './formatInsights';

const { STARTDATE, ENDDATE } = QUERY_PARAMS;

interface IGetInsight {
  clientId: string;
  start: string;
  end: string;
  isLoadingHide?: boolean;
}

const HOST = API_BASE_URL[environment as keyof typeof API_BASE_URL];

const insightsWorker = new Worker('/insights-worker.js');

const insightsEventEmitter = new EventEmitter();

insightsEventEmitter.on('message', data => {
  const { config, endpoint, service, accountId } = data;

  const errorInsight = {
    accountId,
    channel: service,
    datasource: service,
    error: 'Something went wrong',
    isSubAccount: false,
    metrics: {},
    data: {},
  };

  fetch(endpoint, config)
    .then(res => res.json())
    .then(res => {
      if (/error/gi.test(res.className)) {
        insightsEventEmitter.emit('response', errorInsight);
      } else {
        insightsEventEmitter.emit('response', JSON.parse(JSON.stringify(res)));
      }
    })
    .catch(error => insightsEventEmitter.emit('response', errorInsight));
});

export const useInsights = () => {
  const [insights, setInsights] = useRecoilState(InsightsState);
  const setOldInsights = useSetRecoilState(OldInsightsState);
  const [selectedInsight, setSelected] = useState<IInsights>({});
  const [selectedAccountId, setAccountId] = useRecoilState(SelectedAccountId);
  const setSelectedInsights = useSetRecoilState(SelectedInsightsState);
  const setIsLoader = useSetRecoilState(IsLoader);
  const setIsChartLoading = useSetRecoilState(IsChartLoading);
  const [metricsData, setMetricsData] = useRecoilState(MetricsChartState);
  const setMetricsList = useSetRecoilState(AllMetricsListState);
  const setInsightAccountLoader = useSetRecoilState(InsightAccountLoader);

  const { state: clientState } = ClientContainer.useContainer();
  const [dateRange, setDateRange] = useRecoilState(InsightsDateRange);
  const { start, end } = dateRange ?? {};
  const { id } = clientState.selectedClient ?? {};

  const { get: getStorage, set } = useLocalStorage();
  const { get } = useNetwork();
  const { get: insightsGet } = useNetwork();
  const { currency } = useCurrency();
  const { newInsights, insightsUrl } = endpoints();
  const { id: accountId }: any =
    getStorage(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {};

  const localStorageClient: any = useMemo(
    () => getStorage(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {},
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange]
  );
  const qParams = parseQueryParameters(qs.stringify(localStorageClient));

  useEffect(() => {
    if (insights?.length > 0) {
      const found = insights.find(
        (insight: any) => insight.accountId === accountId
      );
      if (found) {
        setSelected(found);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, insights]);

  const loaderHide = useCallback(() => {
    setIsLoader(false);
    setIsChartLoading(false);
  }, [setIsChartLoading, setIsLoader]);

  useEffect(() => {
    // insightsEventEmitter.on('response', sourceInsight => {
    //   console.log('------------------------', sourceInsight);
    //   let obj = formatInsights([sourceInsight.data]);
    //   if (sourceInsight.error) {
    //     obj = [sourceInsight];
    //   }

    //   setInsights(prevState => [...prevState, ...obj]);
    //   loaderHide();
    // });

    // return
    insightsWorker.onmessage = (event: MessageEvent) => {
      const { data: sourceInsight } = event;

      let obj: any[] = [];
      if (sourceInsight.data.error) {
        obj = [sourceInsight.data];
      } else {
        obj = formatInsights([sourceInsight.data]);
      }

      const { accountId: insightAccountId } = sourceInsight.data;
      setInsightAccountLoader(prevState => ({
        ...prevState,
        [insightAccountId]: false,
      }));

      setInsights(prevState => {
        const toUpdateIdx = prevState.findIndex(
          insight => insight.accountId === insightAccountId
        );
        const clonePrevState = [...prevState];
        if (toUpdateIdx !== -1) {
          clonePrevState.splice(toUpdateIdx, 1);
        }
        return [...clonePrevState, ...obj];
      });
      loaderHide();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSingleSourceInsights = async ({
    sourceAccountId,
    service,
    clientId,
    startDate,
    endDate,
    isSubAccount = false,
  }: {
    sourceAccountId: string;
    service: string;
    clientId: string;
    startDate: string;
    endDate: string;
    isSubAccount: boolean;
  }) => {
    const API_HOST = `${HOST}${newInsights}`;
    const accessToken = new Cookies().get('accessToken');

    const config: { [key: string]: any } = {};
    config.headers = {
      Authorization: `Bearer ${accessToken}`,
    };
    const endpoint = `${API_HOST}/${sourceAccountId}?type=insights&clientId=${
      id ?? clientId
    }&startDate=${startDate}&endDate=${endDate}&includeDefault=true&isSubAccount=${isSubAccount}`;
    const payload = {
      endpoint,
      config,
      accountId: sourceAccountId,
      service,
      clientId,
    };
    insightsWorker.postMessage(payload);
  };

  const getInsights = async ({
    start: start1,
    end: end1,
    clientId,
    isLoadingHide,
  }: IGetInsight) => {
    let startDate = DateTime.fromISO(start).toFormat('yyyy-MM-dd');
    let endDate = DateTime.fromISO(end).toFormat('yyyy-MM-dd');
    if (isLoadingHide) {
      setIsChartLoading(true);
    } else {
      setIsLoader(true);
    }

    if (start1 && end1) {
      startDate = DateTime.fromISO(start1).toFormat('yyyy-MM-dd');
      endDate = DateTime.fromISO(end1).toFormat('yyyy-MM-dd');
    }
    startDate = DateTime.fromISO(startDate).toFormat('yyyy-MM-dd');
    endDate = DateTime.fromISO(endDate).toFormat('yyyy-MM-dd');

    if ((id || clientId) && startDate && endDate) {
      const accountLoader: Utils.DynamicObject<string, boolean> = {};
      const accounts: string[] =
        clientState.selectedClient.accounts?.map((account: any) => {
          accountLoader[account.id] = true;
          return {
            subAccount: account.subAccount,
            sourceAccountId: account.id,
            service: account.service,
          };
        }) ?? [];

      const API_HOST = `${HOST}${newInsights}`;
      const accessToken = new Cookies().get('accessToken');

      const config: { [key: string]: any } = {};
      config.headers = {
        Authorization: `Bearer ${accessToken}`,
      };

      setInsightAccountLoader(accountLoader);

      accounts.forEach(account => {
        const { sourceAccountId, service, subAccount = false } =
          (account as any) ?? {};
        const endpoint = `${API_HOST}/${sourceAccountId}?type=insights&clientId=${
          id ?? clientId
        }&startDate=${startDate}&endDate=${endDate}&includeDefault=true&isSubAccount=${subAccount}`;
        const payload = {
          endpoint,
          config,
          accountId: sourceAccountId,
          service,
          clientId,
        };
        // insightsEventEmitter.emit('message', payload);
        insightsWorker.postMessage(payload);
      });

      if (accounts.length === 0) {
        loaderHide();
      }

      const metrics = await API.datasourceMetrics.get();
      setMetricsList(metrics);
      if (Object.keys(metricsData).length === 0) {
        API.customMetrics.find().then(res => {
          setMetricsData(res);
        });
      }
    }
    return null;
  };

  const getSelectedInsights = (selectedAccId?: string) => {
    if (selectedAccId) {
      const found = insights?.find(
        insight => insight.accountId === selectedAccId
      );
      if (found) {
        return found;
      }
    }
    if (selectedAccountId[selectedInsight.channel] && insights?.length > 0) {
      const found = insights?.find(
        insight =>
          insight.accountId === selectedAccId ??
          selectedAccountId[selectedInsight.channel]
      );
      if (found) {
        return found;
      }
    }
    return selectedInsight;
  };

  const getMetricInsight = async (
    datasourceId: string,
    clientId: string,
    _startDate: string,
    _endDate: string,
    metric: string,
    isSubAccount = false
  ) => {
    setIsLoader(true);
    if (insights?.length > 0) {
      const found = insights?.find(
        (insight: any) => insight.accountId === datasourceId
      );
      if (found && found.channel === 'csv' && found.data) {
        const { config, data: csvMetricData } = found.data;
        const { timeBasedKey, dateFormat } = config;
        const perDay = csvMetricData?.map((csvData: any) => {
          const metricValue =
            csvData.find((data: any) => data.metric === metric)?.value ?? 0;
          const date = csvData.find((data: any) => data.metric === timeBasedKey)
            ?.value;
          return {
            time: DateTime.fromFormat(date, dateFormat, {
              zone: 'utc',
            }).toMillis(),
            value: parseInt(metricValue, 10),
          };
        });
        setIsLoader(false);
        return {
          perDay,
        };
      }
      if (
        found &&
        found.startDate === _startDate &&
        found.endDate === _endDate
      ) {
        const metricData = found.metrics?.[metric];
        if (metricData) {
          setIsLoader(false);
          return metricData;
        }
      }

      const startDate = DateTime.fromISO(_startDate).toFormat('yyyy-MM-dd');
      const endDate = DateTime.fromISO(_endDate).toFormat('yyyy-MM-dd');
      const insight = await insightsGet(
        `${newInsights}/${datasourceId}/?type=insights&clientId=${clientId}&startDate=${startDate}&endDate=${endDate}&metrics=${metric}&excludeContent=true&excludePast=true&currency=${currency.code}&isSubAccount=${isSubAccount}`
      );
      setIsLoader(false);
      return insight.metrics?.[metric];
    }

    setIsLoader(false);
    return null;
  };

  const formatDateRange = useCallback((range: any) => {
    let { start: startDate, end: endDate } = range;
    const { period } = range;

    startDate = DateTime.fromISO(startDate).toFormat('yyyy-MM-dd');
    endDate = DateTime.fromISO(endDate).toFormat('yyyy-MM-dd');

    return {
      startDate,
      endDate,
      period,
    };
  }, []);

  const changeDateRange = useCallback(
    (range: any) => {
      const {
        startDate: oldStart,
        endDate: oldEnd,
        period: oldPeriod,
      } = formatDateRange(dateRange);
      const {
        startDate: newStart,
        endDate: newEnd,
        period: newPeriod,
      } = formatDateRange(range);
      if (
        oldStart === newStart &&
        oldEnd === newEnd &&
        oldPeriod === newPeriod
      ) {
        return;
      }

      const workspace = getStorage(LOCAL_STORAGE_TYPES.WORKSPACE) ?? {};
      setOldInsights([]);
      setDateRange(range);
      const startDate = DateTime.fromISO(range.start).toFormat('yyyy-MM-dd');
      const endDate = DateTime.fromISO(range.end).toFormat('yyyy-MM-dd');
      const updatedParams: any = mergeParameters(workspace, {
        [STARTDATE.name]: startDate,
        [ENDDATE.name]: endDate,
        period: range.period,
      });
      set(LOCAL_STORAGE_TYPES.WORKSPACE, updatedParams);
      getInsights({
        clientId: id,
        start: startDate,
        end: endDate,
      });
      trackEvent('date-range', {
        start: startDate,
        end: endDate,
        period: updatedParams.period,
        today: new Date().toISOString(),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [end, qParams, set, setDateRange, start, dateRange]
  );

  const clearInsights = useCallback(() => {
    setInsights([]);
    setAccountId({});
    setSelected({});
    setSelectedInsights({});
  }, [setAccountId, setInsights, setSelectedInsights]);

  const getOldInsights = useCallback(
    ({ start: _start, end: _end, clientId }: IGetInsight) => {
      setIsLoader(true);

      const startDate = DateTime.fromISO(_start).toFormat('yyyy-MM-dd');
      const endDate = DateTime.fromISO(_end).toFormat('yyyy-MM-dd');
      get(
        `${insightsUrl}/?type=insights&clientId=${
          id ?? clientId
        }&startDate=${startDate}&endDate=${endDate}`
      )
        .then(res => {
          setOldInsights(res);
          setIsLoader(false);
        })
        .catch(() => loaderHide());
    },
    [get, id, insightsUrl, loaderHide, setIsLoader, setOldInsights]
  );

  return {
    getInsights,
    getMetricInsight,
    clearInsights,
    changeDateRange,
    getOldInsights,
    getSelectedInsights,
    getSingleSourceInsights,
  };
};

export const useSetInsights = () => {
  const setInsights = useSetRecoilState(InsightsState);

  const removeAccountInsight = useCallback(
    (accountId: string) => {
      setInsights(prevState => {
        const foundInsightIndex = prevState.findIndex(
          insight => insight.accountId === accountId
        );
        if (foundInsightIndex === -1) {
          return prevState;
        }
        const clone = [...prevState];
        clone.splice(foundInsightIndex, 1);
        return [...clone];
      });
    },
    [setInsights]
  );

  return {
    removeAccountInsight,
  };
};
